import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { PeekBox, PeekButton, PeekDialog, PeekDialogActions, PeekDialogContent, PeekStack, PeekTypography, useMediaQuery, } from "@piiqtechnologies/ui-components";
import { Monitor } from "@piiqtechnologies/svg-icons";
import { STORAGE } from "@constants/Storage";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import { matchGatedRoute } from "./gatedRoutes";
import messages from "./messages.i18n";
/**
 * App-wide overlay that nudges phone users to open data-rich Discover reports on
 * desktop. Route-driven: mounted once at the router root, it decides on its own
 * whether to show based on the current path, viewport, and a per-session dismiss.
 *
 * Soft gate (default): a "Continue anyway" button dismisses it for the session.
 * Hard gate (per-route flag): no escape hatch, for views too broken to enter.
 *
 * Phase 0 of responsive support: full mobile layouts are tracked separately.
 * TODO: retire routes from `gatedRoutes` as each becomes responsive.
 */
export const MobileGate = () => {
    const intl = useIntl();
    const { pathname } = useLocation();
    const track = useTrackEvent();
    const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const gatedRoute = useMemo(() => matchGatedRoute(pathname), [pathname]);
    const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(STORAGE.MOBILE_GATE_DISMISSED) === "true");
    const open = isMobileView && Boolean(gatedRoute) && !dismissed;
    const trackedPathRef = useRef(null);
    useEffect(() => {
        if (!open || trackedPathRef.current === pathname)
            return;
        trackedPathRef.current = pathname;
        track(AnalyticsArea.MobileGate, "Shown", { path: pathname });
    }, [open, pathname, track]);
    const handleContinueAnyway = () => {
        track(AnalyticsArea.MobileGate, "Continue Anyway", { path: pathname });
        sessionStorage.setItem(STORAGE.MOBILE_GATE_DISMISSED, "true");
        setDismissed(true);
    };
    if (!open)
        return null;
    return (React.createElement(PeekDialog, { open: true, fullWidth: true, title: " ", componentsProps: { backdrop: { style: { backgroundColor: "rgba(255, 255, 255, 1)" } } }, sx: {
            ".MuiPaper-root": { maxWidth: "340px", borderRadius: "8px", m: 2, position: "relative" },
            ".MuiDialogTitle-root": { marginBottom: -2 },
        } },
        React.createElement(PeekBox, { component: "img", sx: {
                position: "fixed",
                top: 20,
                left: 10,
                width: "100px",
                height: "auto",
                zIndex: (theme) => theme.zIndex.modal + 1,
            }, 
            // @ts-ignore
            src: "/images/peek_discover_dark_logo.svg", alt: "Peek Logo" }),
        React.createElement(PeekDialogContent, { sx: { padding: "45px" } },
            React.createElement(PeekStack, { alignItems: "center", spacing: 1.5 },
                React.createElement(PeekBox, { sx: {
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "radial-gradient(58.33% 58.33% at 50% 50%, #7BE0C3 0%, rgba(255, 255, 255, 0.00) 85%)",
                    } },
                    React.createElement(Monitor, { width: 40, height: 40 })),
                React.createElement(PeekTypography, { variant: "h2", textAlign: "center", fontWeight: "fontWeightMedium" }, intl.formatMessage(messages.title)),
                React.createElement(PeekTypography, { variant: "body1", textAlign: "center", color: "info.main" }, intl.formatMessage(messages.description)))),
        !(gatedRoute === null || gatedRoute === void 0 ? void 0 : gatedRoute.hardBlock) && (React.createElement(PeekDialogActions, { sx: { flexDirection: "column", alignItems: "center", pb: 3 } },
            React.createElement(PeekButton, { fullWidth: true, size: "large", variant: "outlined", color: "secondary", onClick: handleContinueAnyway, sx: { maxWidth: "280px" } }, intl.formatMessage(messages.continueAnyway))))));
};
export default MobileGate;
